import CollapsibleCard from '@/components/shared/CollapsibleCard'
import BackButton from '@/components/ui/BackButton'
import { isReadOnly } from '@/lib/permissions'
import { formatDateTime } from '@/lib/utils'
import { useFindMergeCandidateById, useKeepSeparateMutation, useMergePatientsMutation } from '@/services/deduplication'
import { borderRadius, colors, fontSizes, fontWeights } from '@/theme'
import type { CandidatePatient, DuplicateCandidate } from '@/types/deduplication'
import { CheckCircleOutline, HelpOutline, Merge, Person, Warning } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Radio,
  Select,
  Tooltip,
  Typography
} from '@mui/material'
import type { AxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { gridColumns } from './grid'
import {
  applyOverrides,
  getMergeBlockers,
  getMergeResolution,
  getPrimaryDefaultOverrides,
  parseSafeguarding,
  type EffectiveField,
  type MergeField,
  type MergeFieldSection
} from './merge-fields'
import { buildMergeOverrides, parseConflictFields } from './merge-policy'
import { DeferDialog, DontMergeDialog, KeepSeparateDialog, MergeConfirmDialog } from './sections/confirmation-dialogs'
import MedicationRequestsCardsRow from './sections/medication-requests-cards-row'

const AUTOMATIC = '__automatic__'

const cellSx = { px: 1.5, py: 1, borderRadius: borderRadius.sm } as const
const highlightedCellSx = { ...cellSx, bgcolor: colors.highlight.bg, border: `1px solid ${colors.highlight.border}` }
const defaultCellSx = { ...cellSx, bgcolor: colors.grey[50], border: `1px solid ${colors.border.default}` }
const mergedCellSx = { ...cellSx, bgcolor: colors.grey[100], border: `1px solid ${colors.border.default}` }

interface DialogState {
  merge: boolean
  dontMerge: boolean
  defer: boolean
  keepSeparate: { open: boolean; patientIdx: number }
}

const SECTIONS: { id: MergeFieldSection; title: string; note?: string }[] = [
  { id: 'details', title: 'Patient details' },
  {
    id: 'address',
    title: 'Address',
    note: 'The address is merged as a whole. Choosing a record pins every part of the address to it.'
  },
  { id: 'verification', title: 'Verification status' },
  { id: 'support', title: 'Support & safeguarding' }
]

const CHIP_SX = { height: 20, fontSize: fontSizes.xs, borderRadius: borderRadius.sm } as const

function StateChip({ field, primaryHash }: { field: EffectiveField; primaryHash: string | null }) {
  if (field.overridden) {
    // Pinned to the primary on a field that follows it: the agent chose the record, not the value.
    const fromPrimary = field.field.defaultsToPrimary && field.effectiveSource === primaryHash
    return (
      <Chip
        label={fromPrimary ? 'From primary patient' : 'Chosen by you'}
        size='small'
        sx={{ ...CHIP_SX, bgcolor: colors.brand.primaryLight }}
      />
    )
  }
  switch (field.state) {
    case 'conflict':
      return (
        <Chip
          label='Needs your decision'
          size='small'
          sx={{ ...CHIP_SX, bgcolor: colors.status.errorLight, color: colors.status.error }}
        />
      )
    case 'combined':
      return <Chip label='Combined' size='small' sx={{ ...CHIP_SX, bgcolor: colors.status.infoLight }} />
    case 'locked':
      return <Chip label='Always kept' size='small' sx={{ ...CHIP_SX, bgcolor: colors.status.successLight }} />
    case 'auto':
    case 'paired':
      return <Chip label='Automatic' size='small' sx={{ ...CHIP_SX, bgcolor: colors.grey[200] }} />
    default:
      return null
  }
}

function SafeguardingQAList({ items }: { items: { question: string; answer: string }[] }) {
  return (
    <Box component='ul' sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
      {items.map(({ question, answer }) => (
        <Box component='li' key={question} sx={{ py: 0.25 }}>
          <Typography sx={{ fontSize: fontSizes.sm }}>
            {question} <strong>{answer}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

function FieldValue({ field, value }: { field: MergeField; value: string | null }) {
  if (value === null) return <Typography sx={{ fontSize: fontSizes.base }}>None</Typography>

  if (field.key === 'safeguarding_questions') {
    const parsed = parseSafeguarding(value)
    if (parsed) return <SafeguardingQAList items={parsed} />
  }

  return (
    <Typography sx={{ fontSize: fontSizes.base, whiteSpace: field.multiline ? 'pre-wrap' : 'normal' }}>
      {field.format ? field.format(value) : value}
    </Typography>
  )
}

function MergeFieldRow({
  field,
  patients,
  primaryHash,
  onOverride,
  showErrors,
  disabled
}: {
  field: EffectiveField
  patients: CandidatePatient[]
  primaryHash: string | null
  onOverride: (key: string, patientHash: string | null) => void
  showErrors: boolean
  disabled?: boolean
}) {
  const { values, choices, state } = field
  const isSame = state === 'same' || values.every(value => value === values[0])
  const showError = showErrors && field.unresolved
  const selectValue = field.overridden ? (field.effectiveSource as string) : AUTOMATIC

  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: gridColumns(patients.length, true), alignItems: 'flex-start' }}
      id={`merge-field-${field.field.key}`}
    >
      <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary, pl: 3, py: 1.5 }}>
        {field.field.label}
      </Typography>

      {patients.map((patient, index) => (
        <Box key={patient.hash} sx={{ px: 1.5, py: 1.5 }}>
          <Box sx={!isSame && values[index] !== values[0] ? highlightedCellSx : defaultCellSx}>
            <FieldValue field={field.field} value={values[index]} />
          </Box>
        </Box>
      ))}

      <Box sx={{ px: 1.5, py: 1.5, bgcolor: colors.grey[50] }}>
        <Box sx={showError ? { ...mergedCellSx, border: `1px solid ${colors.status.error}` } : mergedCellSx}>
          {state === 'pending' ? (
            <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.muted }}>
              Mark a primary patient to see the result
            </Typography>
          ) : (
            <FieldValue field={field.field} value={field.effectiveValue} />
          )}
        </Box>

        {state !== 'pending' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
            <StateChip field={field} primaryHash={primaryHash} />
            {field.reason && !field.overridden && (
              <Tooltip title={field.reason} arrow>
                <HelpOutline sx={{ fontSize: 14, color: colors.text.muted, cursor: 'help' }} />
              </Tooltip>
            )}
            {field.overridable && choices.length > 1 && (
              <Select
                value={selectValue}
                onChange={event =>
                  onOverride(field.field.key, event.target.value === AUTOMATIC ? null : event.target.value)
                }
                size='small'
                displayEmpty
                disabled={disabled}
                error={showError}
                renderValue={value => {
                  if (value === AUTOMATIC) {
                    return state === 'conflict' ? 'Choose a record' : 'Automatic'
                  }
                  return choices.find(choice => choice.hash === value)?.label ?? 'Automatic'
                }}
                sx={{
                  minWidth: 150,
                  fontSize: fontSizes.sm,
                  bgcolor: colors.background.paper,
                  borderRadius: borderRadius.sm
                }}
              >
                <MenuItem value={AUTOMATIC} sx={{ fontSize: fontSizes.sm }}>
                  {state === 'conflict'
                    ? 'Choose a record'
                    : state === 'combined'
                      ? 'Keep both (default)'
                      : 'Automatic'}
                </MenuItem>
                {choices.map(choice => (
                  <MenuItem key={choice.hash} value={choice.hash} sx={{ fontSize: fontSizes.sm }}>
                    {choice.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        )}

        {showError && (
          <Typography sx={{ fontSize: fontSizes.sm, color: colors.status.error, mt: 0.5 }}>
            Both records have a value here — pick the one to keep.
          </Typography>
        )}
      </Box>
    </Box>
  )
}

function Callout({
  tone,
  icon,
  children
}: {
  tone: 'info' | 'warning' | 'error'
  icon: ReactNode
  children: ReactNode
}) {
  const bg =
    tone === 'error' ? colors.status.errorLight : tone === 'warning' ? colors.status.warningLight : 'transparent'
  return (
    <Box
      sx={{
        px: 3,
        py: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        bgcolor: bg,
        borderRadius: borderRadius.sm
      }}
    >
      {icon}
      <Box>{children}</Box>
    </Box>
  )
}

export default function MergeReviewPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { groupId } = useParams<{ groupId: string }>()
  const readOnly = isReadOnly()

  const mergeMutation = useMergePatientsMutation()
  const keepSeparateMutation = useKeepSeparateMutation()

  const navState = location.state as { candidate?: DuplicateCandidate; fallbackPath?: string } | null
  const candidateFromState = navState?.candidate
  const fallbackPath = navState?.fallbackPath ?? '/deduplication'

  const groupIdNum = groupId ? Number(groupId) : Number.NaN
  // Always look it up: the candidate arrives via `<Link state>`, which React Router keeps in
  // `window.history.state` across a reload — seeding from it pinned the page to a stale snapshot.
  const shouldLookup = Number.isFinite(groupIdNum)
  const { data: candidateFromQuery, isLoading: isLookupLoading } = useFindMergeCandidateById(
    shouldLookup ? groupIdNum : null,
    candidateFromState?.patient_a?.hash
  )

  const candidate: DuplicateCandidate | undefined = candidateFromQuery ?? candidateFromState ?? undefined
  const patientName = candidate
    ? `${candidate.patient_a.first_name ?? ''} ${candidate.patient_a.last_name ?? ''}`.trim()
    : ''

  const [removedPatientHashes, setRemovedPatientHashes] = useState<Set<string>>(new Set())

  const activePatients = useMemo(() => {
    if (!candidate) return []
    return [candidate.patient_a, candidate.patient_b].filter(p => !removedPatientHashes.has(p.hash))
  }, [candidate, removedPatientHashes])

  const [primaryPatientHash, setPrimaryPatientHash] = useState<string | null>(null)
  const [overrides, setOverrides] = useState<Record<string, string>>({})
  const [showErrors, setShowErrors] = useState(false)
  const [dialogs, setDialogs] = useState<DialogState>({
    merge: false,
    dontMerge: false,
    defer: false,
    keepSeparate: { open: false, patientIdx: 0 }
  })

  const resolutions = useMemo(
    () => getMergeResolution(activePatients, primaryPatientHash),
    [activePatients, primaryPatientHash]
  )
  const fields = useMemo(
    () => applyOverrides(resolutions, overrides, activePatients),
    [resolutions, overrides, activePatients]
  )
  const blockers = useMemo(() => getMergeBlockers(activePatients), [activePatients])
  const unresolved = useMemo(() => fields.filter(field => field.unresolved), [fields])

  const mergeError = (mergeMutation.error as AxiosError<{ message?: string }> | null)?.response?.data ?? null
  const conflictFields = parseConflictFields(mergeError?.message)

  const openDialog = useCallback((key: keyof Omit<DialogState, 'keepSeparate'>) => {
    setDialogs(d => ({ ...d, [key]: true }))
  }, [])

  const closeDialog = useCallback((key: keyof Omit<DialogState, 'keepSeparate'>) => {
    setDialogs(d => ({ ...d, [key]: false }))
  }, [])

  // Which record survives decides every field, so a new primary invalidates the old choices — bar
  // the ones it answers itself, which are re-seeded from it.
  const handlePrimaryChange = useCallback(
    (patientHash: string) => {
      setPrimaryPatientHash(patientHash)
      setOverrides(getPrimaryDefaultOverrides(activePatients, patientHash))
      setShowErrors(false)
    },
    [activePatients]
  )

  const handleOverride = useCallback((key: string, patientHash: string | null) => {
    setOverrides(prev => {
      if (patientHash === null) {
        const { [key]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [key]: patientHash }
    })
  }, [])

  // So the failure banner never describes a request that no longer matches the screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the inputs change
  useEffect(() => {
    if (mergeMutation.isError) mergeMutation.reset()
  }, [overrides, primaryPatientHash])

  const handleMergeClick = useCallback(() => {
    if (readOnly) return
    if (!primaryPatientHash) {
      enqueueSnackbar('Please select a primary patient before merging.', { variant: 'warning' })
      return
    }
    if (activePatients.length < 2) return

    if (unresolved.length > 0) {
      setShowErrors(true)
      enqueueSnackbar(`${unresolved.length} field(s) still need a decision.`, { variant: 'warning' })
      const first = document.getElementById(`merge-field-${unresolved[0].field.key}`)
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setShowErrors(false)
    openDialog('merge')
  }, [readOnly, primaryPatientHash, activePatients, unresolved, openDialog, enqueueSnackbar])

  const handleMergeConfirm = useCallback(() => {
    if (readOnly) return
    if (!primaryPatientHash || activePatients.length < 2) return

    const primaryPatient = activePatients.find(p => p.hash === primaryPatientHash)
    const nonPrimaryPatient = activePatients.find(p => p.hash !== primaryPatientHash)
    if (!primaryPatient || !nonPrimaryPatient) return

    mergeMutation.mutate(
      {
        mergeHash: nonPrimaryPatient.hash,
        body: {
          keepPatientHash: primaryPatient.hash,
          fieldOverrides: buildMergeOverrides(fields, activePatients)
        }
      },
      {
        onSuccess: () => {
          closeDialog('merge')
          enqueueSnackbar('Patient records merged successfully.', { variant: 'success' })
          navigate(fallbackPath)
        },
        onError: () => {
          closeDialog('merge')
        }
      }
    )
  }, [
    readOnly,
    primaryPatientHash,
    activePatients,
    fields,
    mergeMutation,
    closeDialog,
    enqueueSnackbar,
    navigate,
    fallbackPath
  ])

  const handleDontMergeConfirm = useCallback(() => {
    if (readOnly) return
    closeDialog('dontMerge')
    if (activePatients.length < 2) return

    keepSeparateMutation.mutate(
      {
        hash: activePatients[0].hash,
        body: { basePatientHash: activePatients[1].hash, groupByUser: true }
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Patient records removed from duplicates', { variant: 'success' })
          navigate(fallbackPath)
        }
      }
    )
  }, [readOnly, closeDialog, activePatients, keepSeparateMutation, enqueueSnackbar, navigate, fallbackPath])

  const handleDeferConfirm = useCallback(() => {
    closeDialog('defer')
    enqueueSnackbar('Patient records deferred', { variant: 'success' })
    navigate(fallbackPath)
  }, [closeDialog, enqueueSnackbar, navigate, fallbackPath])

  const handleKeepSeparateConfirm = () => {
    if (readOnly) return
    const patientToRemove = activePatients[dialogs.keepSeparate.patientIdx]
    const basePatient = activePatients.find(p => p.hash !== patientToRemove?.hash)
    if (patientToRemove && basePatient) {
      keepSeparateMutation.mutate(
        { hash: patientToRemove.hash, body: { basePatientHash: basePatient.hash, groupByUser: true } },
        {
          onSuccess: () => {
            setRemovedPatientHashes(prev => new Set([...prev, patientToRemove.hash]))
            if (primaryPatientHash === patientToRemove.hash) setPrimaryPatientHash(null)
            setOverrides({})
            enqueueSnackbar('Patient record removed from duplicates', { variant: 'success' })
          }
        }
      )
    }
    setDialogs(d => ({ ...d, keepSeparate: { open: false, patientIdx: 0 } }))
  }

  if (!candidate) {
    if (shouldLookup && isLookupLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )
    }
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Merge recommendation not found. Please go back and select a candidate from the list.</Typography>
        <Button variant='link' onClick={() => navigate(fallbackPath)}>
          Back to duplicates
        </Button>
      </Box>
    )
  }

  const fieldsBySection = (section: MergeFieldSection) => fields.filter(field => field.field.section === section)
  const historyFields = fieldsBySection('history')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
      <BackButton fallbackPath={fallbackPath} defaultLabel='Back to duplicates' />

      <Typography variant='pageTitle' component='h1'>
        Review Duplicate Patients And Merge
      </Typography>

      {blockers.length > 0 && (
        <Callout tone='error' icon={<Warning sx={{ fontSize: 20, color: colors.status.error, mt: 0.25 }} />}>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>
            These records cannot be merged yet
          </Typography>
          {blockers.map(blocker => (
            <Typography key={blocker.label} sx={{ fontSize: fontSizes.sm, mb: 0.5 }}>
              <strong>{blocker.label}.</strong> {blocker.detail}
            </Typography>
          ))}
        </Callout>
      )}

      {mergeMutation.isError && (
        <Callout tone='error' icon={<Warning sx={{ fontSize: 20, color: colors.status.error, mt: 0.25 }} />}>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>
            The merge was not completed — nothing was changed
          </Typography>
          {conflictFields.length > 0 ? (
            <Typography sx={{ fontSize: fontSizes.sm }}>
              The API could not merge these fields on its own and this screen has no way to resolve them:{' '}
              <strong>{conflictFields.join(', ')}</strong>. Align them on the patient records, then try again.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: fontSizes.sm }}>{mergeError?.message ?? 'Please try again.'}</Typography>
          )}
        </Callout>
      )}

      <Paper variant='outlined' sx={{ borderRadius: borderRadius.md, overflowX: 'auto' }}>
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography sx={{ fontSize: fontSizes.lg, fontWeight: fontWeights.semibold }}>
              Merge recommendation: {patientName} ({activePatients.length})
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns(activePatients.length, true) }}>
          <Box />
          {activePatients.map((patient, idx) => (
            <Box key={patient.hash} sx={{ px: 1.5, py: 2 }}>
              <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.25 }}>
                Patient {idx + 1}: {patient.first_name} {patient.last_name}
              </Typography>
              <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
                Created on: {formatDateTime(candidate.created_at) || 'N/A'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <Radio
                  size='small'
                  checked={primaryPatientHash === patient.hash}
                  onChange={() => handlePrimaryChange(patient.hash)}
                  disabled={readOnly}
                  sx={{ p: 0 }}
                />
                <Typography sx={{ fontSize: fontSizes.sm }}>Mark as primary patient</Typography>
                {idx === 0 && (
                  <Tooltip
                    title='The primary patient is the record that survives. Everything the other record holds is added to it, and the merged record takes its first and last name from it — changing the primary resets the choices below.'
                    arrow
                  >
                    <HelpOutline sx={{ fontSize: 16, color: colors.brand.primary, cursor: 'help' }} />
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
          <Box sx={{ px: 1.5, py: 2, bgcolor: colors.grey[50] }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Merge sx={{ fontSize: 18, color: colors.brand.primary }} />
              <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, color: colors.brand.primary }}>
                Merged patient
              </Typography>
            </Box>
            <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
              {primaryPatientHash
                ? unresolved.length > 0
                  ? `${unresolved.length} field(s) need your decision.`
                  : 'Every field is resolved. You can override any of them.'
                : 'Mark a primary patient to see what the merged record will look like.'}
            </Typography>
          </Box>
        </Box>

        <Callout tone='info' icon={<CheckCircleOutline sx={{ fontSize: 20, color: colors.status.success }} />}>
          <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
            The API merges the two records itself: anything held by only one of them is carried over, red flags and ID
            verification are kept, and medical history from both records is combined. You only have to choose where the
            two records disagree — and you can override any other field from the dropdowns.
          </Typography>
        </Callout>

        {SECTIONS.map(section => {
          const sectionFields = fieldsBySection(section.id)
          if (sectionFields.length === 0) return null
          return (
            <Box key={section.id}>
              <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns(activePatients.length, true), mt: 1 }}>
                <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, pl: 3, py: 1.5 }}>
                  {section.title}
                </Typography>
              </Box>
              {section.note && (
                <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary, px: 3, pb: 1 }}>
                  {section.note}
                </Typography>
              )}
              {sectionFields.map(field => (
                <MergeFieldRow
                  key={field.field.key}
                  field={field}
                  patients={activePatients}
                  primaryHash={primaryPatientHash}
                  onOverride={handleOverride}
                  showErrors={showErrors}
                  disabled={readOnly}
                />
              ))}
            </Box>
          )
        })}

        <Callout tone='warning' icon={<Warning sx={{ fontSize: 20, color: colors.status.warning, mt: 0.25 }} />}>
          <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
            After merging, all order items and questionnaire answers from the non-primary patient will be reassigned to
            the primary patient. The non-primary patient record will be permanently deactivated and cannot be restored.
          </Typography>
        </Callout>

        <MedicationRequestsCardsRow patients={activePatients} showMergedColumn backLabel='Back to merge review' />

        {activePatients.length > 2 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns(activePatients.length, true), py: 1.5 }}>
            <Box />
            {activePatients.map((_, idx) => (
              <Box key={activePatients[idx].hash} sx={{ px: 1.5, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant='secondary'
                  size='small'
                  disabled={readOnly}
                  sx={{ fontSize: fontSizes.sm, textTransform: 'none' }}
                  onClick={() => setDialogs(d => ({ ...d, keepSeparate: { open: true, patientIdx: idx } }))}
                >
                  Keep as separate patient
                </Button>
              </Box>
            ))}
            <Box />
          </Box>
        )}
      </Paper>

      <CollapsibleCard sectionId='medical-history' title='Medical history' defaultExpanded={true}>
        <Box sx={{ px: 3, py: 1, overflowX: 'auto' }}>
          <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary, mb: 2 }}>
            Medical history from both records is combined by default. Pick a record if only one of the two should be
            kept.
          </Typography>
          {historyFields.map(field => (
            <MergeFieldRow
              key={field.field.key}
              field={field}
              patients={activePatients}
              primaryHash={primaryPatientHash}
              onOverride={handleOverride}
              showErrors={showErrors}
              disabled={readOnly}
            />
          ))}
        </Box>
      </CollapsibleCard>

      <CollapsibleCard sectionId='user-details' title='User details' defaultExpanded={false}>
        <Box sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 3 }}>
            {[
              { label: 'First name', getValue: (p: CandidatePatient) => p.first_name ?? '-', icon: true },
              { label: 'Last name', getValue: (p: CandidatePatient) => p.last_name ?? '-', icon: true },
              { label: 'Email address', getValue: (p: CandidatePatient) => p.user?.email ?? '-', icon: true },
              { label: 'Mobile', getValue: (p: CandidatePatient) => p.user?.phone ?? '-', icon: false },
              { label: 'GP', getValue: (p: CandidatePatient) => p.gp_name ?? '-', icon: false }
            ].map(col => {
              const source = primaryPatientHash
                ? activePatients.find(p => p.hash === primaryPatientHash)
                : activePatients[0]
              const val = source ? col.getValue(source) : '-'
              return (
                <Box key={col.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    {col.icon && <Person sx={{ fontSize: 16, color: colors.text.secondary }} />}
                    <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>{col.label}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold }}>{val}</Typography>
                </Box>
              )
            })}
          </Box>
          <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.muted, mt: 2 }}>
            These are the account holder's details, shown for context. They are not part of the merge.
          </Typography>
        </Box>
      </CollapsibleCard>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant='secondary' onClick={() => openDialog('dontMerge')} disabled={readOnly} sx={{ px: 4 }}>
            Don&apos;t Merge Patients
          </Button>
          <Button
            variant='primary'
            startIcon={<Merge />}
            onClick={handleMergeClick}
            disabled={readOnly || blockers.length > 0}
            sx={{ px: 4 }}
          >
            Merge Patients
          </Button>
        </Box>
      </Box>

      <MergeConfirmDialog
        open={dialogs.merge}
        patientCount={activePatients.length}
        changes={fields
          .filter(field => field.overridden || field.state === 'combined')
          .map(field => ({ label: field.field.label, value: field.effectiveValue }))}
        onConfirm={handleMergeConfirm}
        isLoading={mergeMutation.isPending}
        onCancel={() => closeDialog('merge')}
      />
      <DontMergeDialog
        open={dialogs.dontMerge}
        onConfirm={handleDontMergeConfirm}
        onCancel={() => closeDialog('dontMerge')}
      />
      <DeferDialog open={dialogs.defer} onConfirm={handleDeferConfirm} onCancel={() => closeDialog('defer')} />
      <KeepSeparateDialog
        open={dialogs.keepSeparate.open}
        patientNumber={dialogs.keepSeparate.patientIdx + 1}
        onConfirm={handleKeepSeparateConfirm}
        onCancel={() => setDialogs(d => ({ ...d, keepSeparate: { open: false, patientIdx: 0 } }))}
      />
    </Box>
  )
}
